import { NotificationType, NotificationPriority } from './types'
import type { Notification } from './types'
import { isServerNotificationId } from './serverNotifications'

// Realtime transport can redeliver the same event (reconnect replay,
// duplicate Mercure publish); dedupe identical title+message pairs that
// arrive within this window instead of trusting each event to be unique.
const DEDUPE_WINDOW_MS = 3000

export class NotificationService {
  private notifications: Notification[] = []
  private listeners: Set<(notifications: Notification[]) => void> = new Set()

  /** Exposed for unit tests; not part of the public composable contract. */
  findRecentDuplicate(title: string, message: string, type: NotificationType): Notification | null {
    const now = Date.now()
    return (
      this.notifications.find(
        (n) =>
          n.title === title &&
          n.message === message &&
          n.type === type &&
          now - n.createdAt.getTime() <= DEDUPE_WINDOW_MS,
      ) ?? null
    )
  }

  addNotification(
    title: string,
    message: string,
    type: NotificationType = NotificationType.INFO,
    priority: NotificationPriority = NotificationPriority.NORMAL,
    data?: Record<string, unknown>,
    actionUrl?: string,
    actionText?: string,
  ): Notification {
    const duplicate = this.findRecentDuplicate(title, message, type)
    if (duplicate) {
      return duplicate
    }

    const notification: Notification = {
      id: this.generateId(),
      type,
      title,
      message,
      read: false,
      priority,
      createdAt: new Date(),
    }

    if (data) {
      notification.data = data
    }
    if (actionUrl) {
      notification.actionUrl = actionUrl
    }
    if (actionText) {
      notification.actionText = actionText
    }

    // Insert notification in priority order
    this.insertByPriority(notification)

    // Show browser notification if permission granted
    this.showBrowserNotification(notification)

    // Notify listeners
    this.notifyListeners()

    return notification
  }

  /**
   * Подмешивает сохранённые на сервере уведомления (после перезагрузки страницы центр
   * иначе пуст). Прежние серверные записи заменяются, локальные из realtime остаются,
   * кроме тех, что дублируют серверные по заголовку и тексту.
   */
  hydrate(serverNotifications: Notification[]): void {
    const serverKeys = new Set(serverNotifications.map((n) => `${n.title}\u0000${n.message}`))
    const local = this.notifications.filter(
      (n) => !isServerNotificationId(n.id) && !serverKeys.has(`${n.title}\u0000${n.message}`),
    )
    this.notifications = [...local, ...serverNotifications]
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      .slice(0, 100)
    this.notifyListeners()
  }

  markAsRead(notificationId: string): void {
    const notification = this.notifications.find((n) => n.id === notificationId)
    if (notification && !notification.read) {
      notification.read = true
      this.notifyListeners()
    }
  }

  markAllAsRead(): void {
    let hasChanges = false
    this.notifications.forEach((notification) => {
      if (!notification.read) {
        notification.read = true
        hasChanges = true
      }
    })

    if (hasChanges) {
      this.notifyListeners()
    }
  }

  removeNotification(notificationId: string): void {
    const index = this.notifications.findIndex((n) => n.id === notificationId)
    if (index > -1) {
      this.notifications.splice(index, 1)
      this.notifyListeners()
    }
  }

  clearAll(): void {
    if (this.notifications.length > 0) {
      this.notifications = []
      this.notifyListeners()
    }
  }

  clearRead(): void {
    const unreadNotifications = this.notifications.filter((n) => !n.read)
    if (unreadNotifications.length !== this.notifications.length) {
      this.notifications = unreadNotifications
      this.notifyListeners()
    }
  }

  getNotifications(): Notification[] {
    return [...this.notifications]
  }

  getUnreadCount(): number {
    return this.notifications.filter((n) => !n.read).length
  }

  getNotificationsByType(type: NotificationType): Notification[] {
    return this.notifications.filter((n) => n.type === type)
  }

  subscribe(callback: (notifications: Notification[]) => void): () => void {
    this.listeners.add(callback)

    // Immediately call with current notifications
    callback([...this.notifications])

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback)
    }
  }

  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('Browser does not support notifications')
      return false
    }

    if (Notification.permission === 'granted') {
      return true
    }

    if (Notification.permission === 'denied') {
      return false
    }

    const permission = await Notification.requestPermission()
    return permission === 'granted'
  }

  private generateId(): string {
    return `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  private insertByPriority(notification: Notification): void {
    const notifPriority = notification.priority ?? NotificationPriority.NORMAL
    let insertIndex = 0
    for (let i = 0; i < this.notifications.length; i++) {
      const existingPriority = this.notifications[i]?.priority ?? NotificationPriority.NORMAL
      if (existingPriority < notifPriority) {
        insertIndex = i
        break
      }
      insertIndex = i + 1
    }

    this.notifications.splice(insertIndex, 0, notification)

    // Keep only last 100 notifications to prevent memory issues
    if (this.notifications.length > 100) {
      this.notifications = this.notifications.slice(0, 100)
    }
  }

  private showBrowserNotification(notification: Notification): void {
    if (Notification.permission !== 'granted') {
      return
    }

    // Don't show browser notification if page is visible
    if (!document.hidden) {
      return
    }

    try {
      const browserNotification = new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: notification.id,
        requireInteraction: (notification.priority ?? 0) >= NotificationPriority.HIGH,
        silent: (notification.priority ?? NotificationPriority.NORMAL) === NotificationPriority.LOW,
      })

      browserNotification.onclick = () => {
        window.focus()

        // Navigate to action URL if provided
        if (notification.actionUrl) {
          window.location.href = notification.actionUrl
        }

        // Mark as read
        this.markAsRead(notification.id)

        browserNotification.close()
      }

      // Auto-close after 5 seconds for low priority notifications
      if ((notification.priority ?? NotificationPriority.NORMAL) <= NotificationPriority.NORMAL) {
        setTimeout(() => {
          browserNotification.close()
        }, 5000)
      }
    } catch (error) {
      console.error('Failed to show browser notification:', error)
    }
  }

  private notifyListeners(): void {
    const notifications = [...this.notifications]
    this.listeners.forEach((callback) => {
      try {
        callback(notifications)
      } catch (error) {
        console.error('Error in notification listener:', error)
      }
    })
  }

  // Cleanup expired notifications
  cleanupExpired(): void {
    const now = new Date()
    const validNotifications = this.notifications.filter((notification) => {
      return !notification.expiresAt || notification.expiresAt > now
    })

    if (validNotifications.length !== this.notifications.length) {
      this.notifications = validNotifications
      this.notifyListeners()
    }
  }
}
